import { Manager } from '../Manager';
import { IGameState } from '../../State/Store/store';
import { GameItemConfig, ItemIDs } from '../../Config/Items';
import Core from '../../Core/Core';
import { Actions } from '../../State/Actions/Actions';
import Board from '../Board/Board';
import { StuckReasons } from '../../State/reducers/level';
import ResourceTracking from '../Tracking/ResourceTracking';
import { TGameAreas } from '../../DeltaTracking/ResourceParamBuilder';
import { getBoosterAvailable, IStateBoosters } from '../../State/reducers/boosters';
import { HudActions, HudEmitter } from '../Hud/HudEmitter';
import { BoosterUtils } from './BoosterUtils';
import { Hud } from '../Hud/Hud';
import LevelView from '../../View/LevelView';
import AdTracking from '../Tracking/AdTracking';
import { AdContexts, AdGameAreas, AdRewardTypes } from '../../DeltaTracking/AdParamBuilder';
import { Popup } from '../Popups';
import Hint from './Hint';
import Magnet from './Magnet';
import Firecracker from './Firecracker';
import { BoosterTilePostfix } from '../../Tiles/TileSet';

export default class Boosters extends Manager {
    private runningBoosters: string[] = [];
    private magnetRunning: boolean = false;
    private burstPlaying: boolean = false;
    public freeBoosterList: string[] = [];
    public lockList: string[] = [];
    private view: LevelView;
    private hud: Hud;
    private board: Board;
    private hint: Hint;
    private magnet: Magnet;
    private firecracker: Firecracker;

    constructor(view: LevelView, hud: Hud, board: Board) {
        super();

        this.view = view;
        this.hud = hud;
        this.board = board;
        this.hint = new Hint(this.board);
        this.magnet = new Magnet(this.view, this.board);
        this.firecracker = new Firecracker(this.board);

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.removeTilesWithBooster(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.handleBoosterLock(state, pre);
        });

        this.addSelector(
            (state: IStateBoosters, pre: IStateBoosters) => {
                this.handleBoosterRemoved(state, pre);
            },
            (state: IGameState) => state.StateBoosters
        );

        this.addSelector(
            (state: IStateBoosters, pre: IStateBoosters) => {
                this.handleBoosterActivated(state, pre);
            },
            (state: IGameState) => state.StateBoosters
        );

        this.addSelector(
            (state: IStateBoosters, pre: IStateBoosters) => {
                this.handleWaitingBoosters(state, pre);
            },
            (state: IGameState) => state.StateBoosters
        );

        HudEmitter.instance.on(HudActions.GAME_BOOSTER_SELECT, this.handleGameBoosterSelect, this);
    }

    public cleanUp() {
        super.cleanUp();
        this.hint.cleanUp();
        this.hint = null;
        this.magnet.cleanUp();
        this.magnet = null;
        this.firecracker.cleanUp();
        this.firecracker = null;
        this.runningBoosters = [];
        Core.store.dispatch(
            Actions.removeBoosters(Core.store.getState().StateBoosters.currentBoosters)
        );
        HudEmitter.instance.off(HudActions.GAME_BOOSTER_SELECT, this.handleGameBoosterSelect, this);
    }

    public handleGameBoosterSelect(id: ItemIDs) {
        const state = Core.store.getState();
        const { StateBoosters } = state;

        if (!BoosterUtils.canSelectBooster(state, id, this.runningBoosters)) {
            return;
        }

        if (state.StateScore.winTime === -1) {
            Core.store.dispatch(Actions.setWinTime(state.StateLevel.winTime));
        }

        if (StateBoosters.waitingBoosters.includes(ItemIDs.MAGNET)) {
            this.cancelBooster(state, ItemIDs.MAGNET);
            return;
        }

        if (BoosterUtils.isTutorialBooster(StateBoosters, id)) {
            this.addFreeBooster(id);
            return;
        }

        if (getBoosterAvailable(state.StateBoosters, id)) {
            Core.store.dispatch(Actions.addBoosters([id]));
            this.addToRunning(id);
        } else {
            this.handleBuyBooster(id, GameItemConfig[id].price);
        }
    }

    public removeRunningBooster(id: string) {
        this.runningBoosters.splice(this.runningBoosters.indexOf(id), 1);
    }

    public handleBuyBooster(boosterName: ItemIDs, boosterPrice: number) {
        const state = Core.store.getState();
        const hasBooster = () =>
            Core.store.getState().StateBoosters.availableBoosters.includes(boosterName);
        if (!hasBooster() && state.StateCurrency.coin >= boosterPrice) {
            Core.store.dispatch(Actions.updateCoins(-boosterPrice));
            Core.store.dispatch(Actions.silentStoreBooster([boosterName]));
            ResourceTracking.boosterBought(boosterName);
        }
        if (hasBooster()) {
            Core.store.dispatch(Actions.addBoosters([boosterName]));
        } else {
            const { StateCurrency, StatePlayer } = Core.store.getState();
            this.hud.view.addCoinsAnim(StatePlayer.gainedCoins, StateCurrency.coin).catch(err => {
                console.log(['error with coins anim in level', err]);
            });

            const args = this.createBoosterBuyArgs(boosterName, boosterPrice);
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.INSUFFICIENT, 0, args);
        }
    }

    private async handleBoosterRemoved(state: IStateBoosters, previous: IStateBoosters) {
        const removes = BoosterUtils.getBoostersToRemove(state, previous);
        for (const key of removes) {
            if (key === ItemIDs.HIGHLIGHT) {
                await this.view.highlightOutAnimation();
            }
            this.removeRunningBooster(key);
        }
    }

    private handleBoosterActivated(state: IStateBoosters, previous: IStateBoosters) {
        const activated = BoosterUtils.getAddedBoosters(state, previous, 'activeBoosters');
        if (activated.length > 0) {
            // this.resetHelperTimer();
        }
        activated.forEach(key => {
            if (key === ItemIDs.TIME) {
                this.handleTimeBooster();
            }

            if (key === ItemIDs.SHUFFLE) {
                this.handleShuffleBooster();
            }

            if (key === ItemIDs.FIRECRACKER) {
                if (!this.burstPlaying) {
                    this.burstPlaying = true;
                    this.firecracker.triggerTileBursts();
                }
            }
            if (key === ItemIDs.MAGNET) {
                if (!this.magnetRunning) {
                    this.magnetRunning = true;
                    this.view.animateMagnet();
                    this.magnet.triggerMagnetPulls();
                }
            }
        });
    }

    private async handleWaitingBoosters(state: IStateBoosters, previous: IStateBoosters) {
        const waiting = BoosterUtils.getAddedBoosters(state, previous, 'waitingBoosters');
        if (waiting.length === 0) {
            return Promise.resolve();
        }
        const fullId = state.waitingBoosters[0];
        const id = waiting[0];
        let config = { id };
        const [boosterName, postFix] = fullId.toString().split('_');
        config = this.hud.ui.getButtonLoc(config);

        const curstate = Core.store.getState();
        const { StateLevel: level } = curstate;
        switch (boosterName as ItemIDs) {
            case ItemIDs.HIGHLIGHT:
            case ItemIDs.HINT:
            case ItemIDs.FIRECRACKER:
                if (BoosterUtils.checkAutoBooster(boosterName as ItemIDs, level.levelIndex)) {
                    Core.store.dispatch(Actions.activateBooster(id));
                    return true;
                }
                if (postFix === BoosterTilePostfix) {
                    return Core.store.dispatch(Actions.activateBooster(fullId));
                }
                return this.view.createBooster(config).then(() => {
                    Core.store.dispatch(Actions.activateBooster(id));
                });
            case ItemIDs.MAGNET:
                // this.board.showAvailable(curstate);
                if (level.stuck) {
                    this.view.hideStuckPopup(true);
                }
                this.view.screenOverlay(true, 300);
                return new Promise(resolve => {
                    resolve();
                });
            default:
                return new Promise(resolve => {
                    Core.store.dispatch(Actions.activateBooster(id));
                    resolve();
                });
        }
    }

    private addToRunning(id: string) {
        if (id === ItemIDs.FIRECRACKER || id === ItemIDs.MAGNET) {
            this.runningBoosters.push(id);
        }
    }

    private handleBoosterLock(state: IGameState, previous: IGameState) {
        const unlockId = BoosterUtils.checkBoosterInGameUnlock(state.StateLevel);
        if (state.StateBoosters.lockedBoosters.includes(unlockId)) {
            this.hud.ui.unlockBoosters([unlockId], true);
        }
    }

    private async handleTimeBooster() {
        Core.store.dispatch(Actions.addWinTime(GameItemConfig[ItemIDs.TIME].strength));
        await this.view.showTime();
        Core.store.dispatch(Actions.setLevelStuck({ stuck: false, reason: StuckReasons.TIME }));
        Core.store.dispatch(Actions.removeBoosters([ItemIDs.TIME]));
        ResourceTracking.sendUsedResourceEvent(ItemIDs.TIME, 1, TGameAreas.LEVEL);
    }

    private async handleShuffleBooster() {
        this.board.view.stopHints();
        this.board.shuffle();
        ResourceTracking.sendUsedResourceEvent(ItemIDs.SHUFFLE, 1, TGameAreas.LEVEL);
    }

    private removeTilesWithBooster(state: IGameState, previous: IGameState) {
        if (state.StateLevel.removes.length !== previous.StateLevel.removes.length) {
            if (state.StateLevel.removes.length === 0) {
                const currentBoosters = state.StateBoosters.currentBoosters;
                const boosterToRemove = currentBoosters[currentBoosters.length - 1];
                if (boosterToRemove.indexOf(ItemIDs.FIRECRACKER) !== -1) {
                    this.burstPlaying = false;
                } else if (boosterToRemove.indexOf(ItemIDs.MAGNET) !== -1) {
                    this.magnetRunning = false;
                }
                this.view.screenOverlay(false, 300);
                Core.store.dispatch(Actions.removeBoosters([boosterToRemove]));
            }
        }
    }

    private addFreeBooster(id: ItemIDs) {
        Core.store.dispatch(Actions.silentStoreBooster([id]));
        Core.store.dispatch(Actions.addBoosters([id]));
        this.addToRunning(id);
    }

    private cancelBooster(state: IGameState, id: ItemIDs) {
        Core.store.dispatch(Actions.cancelBooster(id));
        if (state.StateLevel.stuck) {
            this.view.hideStuckPopup(false);
        }
        if (!this.freeBoosterList.includes(id)) {
            Core.store.dispatch(Actions.silentStoreBooster([id]));
        }
        this.runningBoosters.splice(this.runningBoosters.indexOf(id), 1);
        this.view.screenOverlay(false, 300);
        // Board.showAvailable(state);
    }

    private createBoosterBuyArgs(boosterName: ItemIDs, boosterPrice: number) {
        return [
            boosterName,
            async (action: string) => {
                if (action === 'watch') {
                    const adSuccess = () => {
                        this.hud.view.deleteLoadAdText();
                        AdTracking.trackAdWatched(boosterName);
                        Core.store.dispatch(Actions.updateCoins(100));
                        this.handleBuyBooster(boosterName, boosterPrice);
                    };

                    const adError = () => {
                        this.hud.view.deleteLoadAdText();
                        this.hud.view.addAdError();
                    };

                    this.hud.view.createLoadAdText();
                    if (Core.delta) {
                        Core.delta.setAdParams(
                            AdRewardTypes.COINS,
                            AdContexts.LOSS_AVERSION,
                            AdGameAreas.LEVEL,
                            Core.store.getState().StateLevel.levelIndex
                        );
                    }

                    await Core.sdk.ads.showAd('rewarded', 'coins', adSuccess, adError);
                } else {
                    this.handleBuyBooster(boosterName, boosterPrice);
                }
            },
            () => {},
        ];
    }
}
